/* ==========================================================================
   BARIQ PRODUCT PAGE — STAGE 2 (presentation layer)

   Loaded exclusively by sections/bariq-product-polish.liquid, which exists
   only in templates/product.json.

   WHAT THIS FILE DOES
     1. Re-presents the EXISTING lens radio group as grouped premium cards.
     2. Restyles / relabels the EXISTING WhatsApp element.
     3. Re-presents the EXISTING product description as two accordions.

   WHAT THIS FILE NEVER DOES
     - It never creates, clones, removes or re-values a variant input. Every
       radio rendered by Shopify is MOVED, never replaced, so variant state,
       radio semantics, keyboard behaviour, the `form` association and
       `data-option-value-id` all stay exactly as Shopify rendered them.
     - It never selects a variant, never listens for clicks on the cards and
       never keeps its own idea of what is selected. Selection happens purely
       through the native <input> + <label for> relationship, so Shopify
       remains the single source of truth.
     - It contains NO WhatsApp business logic: no number, no wa.me URL, no
       customized detection, no CTA switching. That logic lives in
       sections/main-product.liquid and is only read from, never rewritten.
     - It never touches Camweara, Vairo, prices, inventory or the cart.

   Dawn replaces the whole <variant-selects> element on every variant change
   (product-info.js -> updateOptionValues -> HTMLUpdateUtility.viewTransition),
   so every enhancement here is idempotent and re-applied from a
   MutationObserver plus the Theme Editor section events.
   ========================================================================== */

(function () {
  'use strict';

  /* Re-running the script tag (Theme Editor re-inserting the loader section)
     must re-initialise, not double-bind. */
  if (window.BariqProductPage && window.BariqProductPage.init) {
    window.BariqProductPage.init();
    return;
  }

  /* ------------------------------------------------------------------------
     Lens vocabulary — keyed on the EXACT Shopify option values.
     The raw values are never modified; only their presentation is.
     ---------------------------------------------------------------------- */

  var LENS_VALUES = {
    'anti-blue light': {
      group: 'ready',
      name: 'Blue Cut Lenses',
      sub: 'Clear lenses',
    },
    photochromic: {
      group: 'ready',
      name: 'Photochromic',
      sub: 'Bluecut • Darkens in sunlight',
    },
    'anti-blue light customized ++': {
      group: 'custom',
      name: 'Blue Cut Lenses Customized',
      sub: 'Prescription • Clear',
    },
    'photochromic customized ++': {
      group: 'custom',
      name: 'Photochromic Customized',
      sub: 'Prescription • Photochromic',
    },
  };

  var GROUPS = {
    ready: {
      title: 'Ready to Order',
      note: 'Choose your lens and add it directly to cart.',
    },
    custom: {
      title: 'Customized Prescription',
      note: 'For prescription lenses — send us your eye test on WhatsApp.',
    },
  };

  var LENS_HEADING = 'Lens Options';
  var WA_LABEL = 'Send Prescription on WhatsApp';
  var WA_NOTE =
    'We’ll confirm your prescription and lens details before processing your order.';

  /* Description headings. Order matters: the most specific pattern wins.
     Matching is case-insensitive and tolerates a trailing colon and stray
     whitespace / non-breaking spaces. */
  var HEADINGS = [
    [/^best\s*(?:for\s*)?face\s*shapes?$/, 'fit'],
    [/^size\s*(?:guide|chart|&\s*fit|and\s*fit)$/, 'fit'],
    [/^size\s*&?\s*fit$/, 'fit'],
    [/^fit(?:\s*type)?$/, 'fit'],
    [/^(?:product\s*)?description$/, 'details'],
    [/^key\s*features?$/, 'details'],
  ];

  var ACCORDIONS = [
    { key: 'details', title: 'Product Details', open: true },
    { key: 'fit', title: 'Size & Fit', open: false },
  ];

  var observer = null;

  /* ------------------------------------------------------------------------
     helpers
     ---------------------------------------------------------------------- */

  function norm(value) {
    return String(value == null ? '' : value)
      .replace(/ /g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* ========================================================================
     1. LENS OPTIONS
     ====================================================================== */

  /* A fieldset qualifies only when EVERY one of its radio values is a known
     lens value. Detection is by value, not by option name, because the same
     four values ship under several option names in this catalogue
     (Lens / Lens Type / Lens type / test). Products with no lens values —
     including every product with no second option at all — are left
     completely untouched, so nothing renders and no gap is introduced. */
  function readLensGroup(fieldset) {
    var inputs = Array.prototype.slice.call(
      fieldset.querySelectorAll('input[type="radio"]')
    );
    if (inputs.length < 2) return null;

    var items = [];
    for (var i = 0; i < inputs.length; i++) {
      var meta = LENS_VALUES[norm(inputs[i].value)];
      if (!meta) return null;
      items.push({ input: inputs[i], meta: meta });
    }
    return items;
  }

  /* Dawn renders `<input><label for=…>` as adjacent siblings. Resolve by
     traversal rather than by selector so no id needs escaping. */
  function labelFor(input) {
    var next = input.nextElementSibling;
    if (next && next.tagName === 'LABEL' && next.htmlFor === input.id) return next;

    var labels = input.parentNode
      ? input.parentNode.querySelectorAll('label')
      : [];
    for (var i = 0; i < labels.length; i++) {
      if (labels[i].htmlFor === input.id) return labels[i];
    }
    return null;
  }

  function buildLensCard(item) {
    var input = item.input;
    var label = labelFor(input);
    if (!label) return null;

    var card = el('div', 'bariq-lens__card');

    /* The input keeps every attribute Shopify gave it and stays immediately
       before its own label, so `input:checked + label` still matches. */
    card.appendChild(input);
    card.appendChild(label);

    /* Preserve Dawn's sold-out helper text verbatim. */
    var unavailable = label.querySelector('.label-unavailable');

    label.className = 'bariq-lens__label';
    label.textContent = '';
    label.appendChild(el('span', 'bariq-lens__name', item.meta.name));
    label.appendChild(el('span', 'bariq-lens__sub', item.meta.sub));

    var tick = el('span', 'bariq-lens__tick');
    tick.setAttribute('aria-hidden', 'true');
    label.appendChild(tick);

    if (unavailable) label.appendChild(unavailable);

    return card;
  }

  function enhanceLens(scope) {
    var fieldsets = scope.querySelectorAll(
      'variant-selects .product-form__input:not([data-bariq-lens])'
    );

    Array.prototype.forEach.call(fieldsets, function (fieldset) {
      var items = readLensGroup(fieldset);

      if (!items) {
        /* Mark non-lens groups too, so the observer's "is there work?" check
           stays a single cheap selector. */
        fieldset.setAttribute('data-bariq-lens', 'skip');
        return;
      }
      fieldset.setAttribute('data-bariq-lens', 'on');

      var wrap = el('div', 'bariq-lens');
      var groups = {};

      /* Attach the wrapper first, then move the radios into it, so no input
         is ever detached from the document mid-enhancement. */
      var legend = fieldset.querySelector('legend');
      if (legend) {
        legend.classList.add('bariq-lens__heading');
        legend.textContent = LENS_HEADING;
        fieldset.insertBefore(wrap, legend.nextSibling);
      } else {
        fieldset.insertBefore(wrap, fieldset.firstChild);
      }

      items.forEach(function (item) {
        var key = item.meta.group;

        if (!groups[key]) {
          var group = el('div', 'bariq-lens__group bariq-lens__group--' + key);
          group.appendChild(el('p', 'bariq-lens__group-title', GROUPS[key].title));
          group.appendChild(el('p', 'bariq-lens__group-note', GROUPS[key].note));
          var cards = el('div', 'bariq-lens__cards');
          group.appendChild(cards);
          groups[key] = cards;
          wrap.appendChild(group);
        }

        var card = buildLensCard(item);
        if (card) groups[key].appendChild(card);
      });

      fieldset.classList.add('bariq-lens-field');
    });
  }

  /* ========================================================================
     2. WHATSAPP CTA — presentation only
     ====================================================================== */

  /* The element, its href, its visibility and the CTA switching all belong to
     the existing script in sections/main-product.liquid. Here the element is
     only relabelled, given a class to style, and shadowed by a helper note
     that mirrors the visibility the existing script has already decided. */
  function enhanceWhatsApp() {
    var link = document.getElementById('whatsapp-custom-link');
    if (!link) return;

    if (link.getAttribute('data-bariq-wa') !== 'on') {
      link.setAttribute('data-bariq-wa', 'on');
      link.classList.add('bariq-wa');

      link.textContent = '';
      link.appendChild(buildWhatsAppIcon());
      link.appendChild(el('span', 'bariq-wa__label', WA_LABEL));

      /* Reflect the inline display the existing script sets, so the note can
         follow the CTA without duplicating any of its logic. */
      new MutationObserver(syncWhatsAppNote).observe(link, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    syncWhatsAppNote();
  }

  function buildWhatsAppIcon() {
    var NS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'bariq-wa__icon');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '17');
    svg.setAttribute('height', '17');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    var path = document.createElementNS(NS, 'path');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute(
      'd',
      'M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.79.97-.14.16-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.47c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07s.89 2.4 1.02 2.56c.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.11-.22-.17-.47-.29Z'
    );

    svg.appendChild(path);
    return svg;
  }

  function syncWhatsAppNote() {
    var link = document.getElementById('whatsapp-custom-link');
    if (!link) return;

    var note = document.getElementById('bariq-wa-note');
    if (!note) {
      note = el('p', 'bariq-wa-note', WA_NOTE);
      note.id = 'bariq-wa-note';
    }

    /* Keep the note directly after the CTA even if the existing script moves
       the CTA into a freshly rendered buttons container. */
    if (link.nextSibling !== note) {
      link.parentNode.insertBefore(note, link.nextSibling);
    }

    /* Read-only mirror of the visibility the existing logic has set. */
    var hidden = window.getComputedStyle(link).display === 'none';
    note.classList.toggle('bariq-wa-note--on', !hidden);
  }

  /* ========================================================================
     3. DESCRIPTION ACCORDIONS
     ====================================================================== */

  /* Returns the recognised heading group for a top-level node, or null.
     A heading is a paragraph that STARTS with a <strong>/<b> whose text is one
     of the known section names. Bold runs inside a paragraph (for example
     "• <strong>UV400 Protection:</strong> …") are never headings, so bullet
     copy is never split apart. */
  function headingGroup(node) {
    if (!node || node.nodeType !== 1) return null;
    if (!/^(P|H[1-6]|DIV)$/.test(node.tagName)) return null;

    var text;

    if (/^H[1-6]$/.test(node.tagName)) {
      /* Semantic headings are not used by the current catalogue, but a future
         product may well use them. */
      text = node.textContent;
    } else {
      var strong = node.querySelector('strong, b');
      if (!strong) return null;

      /* Nothing but whitespace may precede the bold run, at any nesting depth.
         This accepts `<p><strong>Description:</strong>…` as well as
         `<p><span><strong>Description</strong>:…` and rejects bold runs that
         appear mid-sentence, so bullet copy is never mistaken for a heading. */
      var range = document.createRange();
      range.setStart(node, 0);
      range.setEndBefore(strong);
      var before = range.toString();
      range.detach && range.detach();
      if (norm(before) !== '') return null;

      text = strong.textContent;
    }

    text = norm(text).replace(/[:：]\s*$/, '').trim();
    if (!text) return null;

    for (var i = 0; i < HEADINGS.length; i++) {
      if (HEADINGS[i][0].test(text)) return HEADINGS[i][1];
    }
    return null;
  }

  function isBlank(node) {
    if (node.nodeType === 3) return norm(node.textContent) === '';
    if (node.nodeType === 1) {
      return (
        norm(node.textContent) === '' &&
        !node.querySelector('img, svg, iframe, video, hr, table')
      );
    }
    return node.nodeType === 8;
  }

  /* Splits the description into ordered segments. Every node lands in exactly
     one segment; a segment inherits the group of the heading that opened it,
     so unrecognised sections (for example "Color Options:") stay with the
     section they follow instead of being dropped. */
  function segment(container) {
    var segments = [];
    var lead = [];
    var current = null;

    Array.prototype.slice.call(container.childNodes).forEach(function (node) {
      var group = headingGroup(node);

      if (group) {
        current = { group: group, nodes: [node] };
        segments.push(current);
      } else if (current) {
        current.nodes.push(node);
      } else {
        lead.push(node);
      }
    });

    return { lead: lead, segments: segments };
  }

  function buildAccordion(spec, nodes) {
    var details = el('details', 'bariq-acc__item');
    details.id = 'BariqAcc-' + spec.key;
    if (spec.open) details.open = true;

    var summary = el('summary', 'bariq-acc__summary');
    summary.appendChild(el('span', 'bariq-acc__label', spec.title));
    var icon = el('span', 'bariq-acc__icon');
    icon.setAttribute('aria-hidden', 'true');
    summary.appendChild(icon);

    var body = el('div', 'bariq-acc__body');
    nodes.forEach(function (node) {
      body.appendChild(node);
    });

    details.appendChild(summary);
    details.appendChild(body);
    return details;
  }

  function enhanceDescription(scope) {
    var containers = scope.querySelectorAll(
      '[data-bariq-description]:not([data-bariq-acc]), .product__description:not([data-bariq-acc])'
    );

    Array.prototype.forEach.call(containers, function (container) {
      container.setAttribute('data-bariq-acc', 'skip');
      if (container.closest('.bariq-acc__body')) return;

      var parsed = segment(container);
      var buckets = { details: [], fit: [] };

      parsed.segments.forEach(function (seg) {
        buckets[seg.group] = buckets[seg.group].concat(seg.nodes);
      });

      /* DEFENSIVE FALLBACK: anything that does not match the expected
         structure keeps its original, unmodified description. Content is
         never hidden and an empty accordion is never rendered. */
      if (!buckets.details.length && !buckets.fit.length) return;

      /* Drop only trailing nodes that carry no content at all (stray empty
         paragraphs left by the rich-text editor). Nothing visible is lost. */
      Object.keys(buckets).forEach(function (key) {
        while (buckets[key].length && isBlank(buckets[key][buckets[key].length - 1])) {
          buckets[key].pop();
        }
      });

      var acc = el('div', 'bariq-acc');
      var built = 0;

      ACCORDIONS.forEach(function (spec) {
        if (!buckets[spec.key].length) return;
        acc.appendChild(buildAccordion(spec, buckets[spec.key]));
        built++;
      });

      if (!built) return;

      /* Leading copy that appears before the first recognised heading stays
         exactly where it was, above the accordions. */
      container.appendChild(acc);
      container.setAttribute('data-bariq-acc', 'on');
      container.classList.add('bariq-description--accordion');
    });
  }

  /* ========================================================================
     init / re-init
     ====================================================================== */

  var PENDING =
    'variant-selects .product-form__input:not([data-bariq-lens]),' +
    '[data-bariq-description]:not([data-bariq-acc]),' +
    '.product__description:not([data-bariq-acc])';

  function run() {
    var scope = document.querySelector('#MainContent') || document;
    try {
      enhanceLens(scope);
    } catch (e) {
      if (window.console) console.warn('[bariq] lens presentation skipped:', e);
    }
    try {
      enhanceDescription(scope);
    } catch (e) {
      if (window.console) console.warn('[bariq] description presentation skipped:', e);
    }
    try {
      enhanceWhatsApp();
    } catch (e) {
      if (window.console) console.warn('[bariq] whatsapp presentation skipped:', e);
    }
  }

  function init() {
    run();

    /* Observed on <body>, not on #MainContent: Dawn's product-swap path
       replaces <main> wholesale, which would leave an observer rooted inside
       it attached to a detached node and silently stop re-applying. */
    var root = document.body;
    if (!root) return;

    if (observer) observer.disconnect();
    observer = new MutationObserver(function () {
      /* Dawn also swaps <variant-selects> on every variant change. The check
         below is one cheap selector; the work itself runs synchronously so the
         cards are never painted in their un-grouped state. */
      if (!document.querySelector(PENDING)) return;
      run();
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  window.BariqProductPage = { init: init, run: run };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme Editor: a section reload replaces the observed subtree. */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
})();
